import {Box, LoadingOverlay, TextInput} from '@mantine/core';
import {BasicButton} from '@packages/ui';
import Image from 'next/image';
import {LIIGA_HOST} from '../../../../../config/constants/hosts';
import {
  REGISTER_COMMUNITY_BENEFIT_IMAGE_PATH,
  REGISTER_COMPANY_BENEFIT_IMAGE_PATH,
  REGISTER_SCOUT_BENEFIT_IMAGE_PATH,
} from '../../../../../config/constants/img';
import {
  displayStartTime,
  maintenanceEndTime,
} from '../../../../../config/constants/maintenance';
import {INQUIRY, STUDENT_CARD} from '../../../../../config/constants/routes';
import {isReleasedNewRegisterFlow} from '../../../../../config/unReleasePage';
import {MICROSOFT_MAIL_VALIDATION_MSG} from '../../../../../core/entities/mypage/schema';
import {
  EMAIL_INPUT_ERROR_TYPE,
  type EmailInputErrorType,
} from '../../../../../core/entities/register/constants';
import {CommonLink} from '../../../../../shared/components/CommonLink';
import {SectionTitle} from '../../../../../shared/components/SectionTitle';
import {createPathWithQuery} from '../../../../../shared/services/createPathWithQuery';
import {useMediaQuery} from '../../../../../shared/services/useMediaQuery';
import {MaintenanceMailAuth} from '../../../../maintenance/components/MaintenanceMailAuth';
import type {Props} from '../../MailAuthPage';

export const MailAuth: React.FC<Props> = ({
  emailInputErrorType,
  setEmailInputErrorType,
  inputEmail,
  setInputEmail,
  handleRegisterClick,
  introduceCampaignMessage,
  introduceId,
  campaignId,
  isSubmitLoading,
}) => {
  const introduceQueryParamUrl =
    introduceId && campaignId
      ? createPathWithQuery('', {
          introduce_id: introduceId,
          campaign_id: campaignId,
        })
      : '';

  const {isPC} = useMediaQuery();
  const maintenanceNow = new Date();
  const isMaintenance =
    maintenanceEndTime > maintenanceNow && maintenanceNow > displayStartTime;

  // 外資就活ネクスト 旧：LiigaのURL
  const stage = process.env.NEXT_PUBLIC_RELEASE_STAGE;
  const toLiigaURL = LIIGA_HOST[stage ?? 'production'] ?? LIIGA_HOST.production;

  const isNewRegisterFlow = isReleasedNewRegisterFlow();

  const emailErrorMessage: Record<EmailInputErrorType, string> = {
    [EMAIL_INPUT_ERROR_TYPE.FORMAT_ERROR]: 'メールアドレスの形式が不正です',
    [EMAIL_INPUT_ERROR_TYPE.SERVER_ERROR]:
      '認証できませんでした。ご記入に誤りがないかご確認ください。',
    [EMAIL_INPUT_ERROR_TYPE.LENGTH_ERROR]: '100文字以内で入力してください',
    [EMAIL_INPUT_ERROR_TYPE.CONFLICT_ERROR]: '登録済みのメールアドレスです',
    [EMAIL_INPUT_ERROR_TYPE.MICROSOFT_ERROR]: MICROSOFT_MAIL_VALIDATION_MSG,
    [EMAIL_INPUT_ERROR_TYPE.UNIVERSITY_ERROR]:
      'ご所属の大学のメールアドレスは外資就活ドットコムに対応していない可能性があります。',
    [EMAIL_INPUT_ERROR_TYPE.NONE]: '',
  };

  return (
    <Box className="w-full max-w-[664px]">
      <LoadingOverlay
        visible={isSubmitLoading}
        overlayBlur={2}
        className="fixed"
      />
      <SectionTitle order={2} title={'新規会員登録'} />
      <Box className="relative bg-white px-16 pc:px-32 py-32 mb-16">
        {introduceId && campaignId && (
          <Box className="text-notification font-bold p-8 m-8 text-base">
            {introduceCampaignMessage}
          </Box>
        )}
        <Box className="text-left pc:text-center">
          {isMaintenance && <MaintenanceMailAuth />}
          {!isNewRegisterFlow && (
            <>
              <p className="font-bold text-md text-center">
                会員登録には国内・海外の所属大学または高等専門学校のメールアドレスが必要です
              </p>
              <p className="text-base py-8">
                あとから普段ご使用のメールアドレスに変更も可能です。
              </p>
            </>
          )}
        </Box>

        <div className="flex flex-col w-full pc:w-[350px] mx-auto">
          {isNewRegisterFlow && (
            <div className="flex justify-start mb-8">
              <p className="text-base text-black-1">メールアドレス</p>
            </div>
          )}
          <TextInput
            type="email"
            className="w-full"
            placeholder={
              isNewRegisterFlow
                ? 'gaishishukatsu@gmail.com'
                : '大学メールアドレス'
            }
            error={emailErrorMessage[emailInputErrorType]}
            value={inputEmail}
            onChange={(e) => {
              setInputEmail(e.currentTarget.value);
              setEmailInputErrorType(
                e.currentTarget.value.length > 100
                  ? EMAIL_INPUT_ERROR_TYPE.LENGTH_ERROR
                  : '',
              );
            }}
            size={isPC ? 'sm' : 'md'} // アプリ&SPWebで入力時にズームされることを防ぐ
          />
        </div>
        {!isNewRegisterFlow &&
          emailInputErrorType === EMAIL_INPUT_ERROR_TYPE.SERVER_ERROR && (
            <Box className="m-8 text-base">
              <p className="font-bold mb-4 text-notification">
                ご所属の大学のメールアドレスは外資就活ドットコムに対応していない可能性があります。以下の方法を検討してください。
              </p>
              <div className="bg-background-1 p-16 rounded-12">
                <p className="font-bold mb-4">国内大学に在学中の方</p>
                <ul className="mb-6">
                  <li>
                    大学発行のメールアドレスをお持ちでない方は、
                    <CommonLink
                      href={STUDENT_CARD}
                      className="hover:underline hover:decoration-primary-2 text-primary-2"
                      children="学生証アップロード"
                    />
                    で登録申請をお願いたします。
                  </li>
                </ul>
                <p className="font-bold mb-4">海外大学に在学中の方</p>
                <p>
                  大学発行メールアドレスで認証エラーが発生する方は、
                  <CommonLink
                    href={`/inquiry/foreign_univ${introduceQueryParamUrl}`}
                    className="hover:underline hover:decoration-primary-2 text-primary-2"
                    children="海外大からの大学認証"
                  />
                  で登録申請をお願いいたします。
                </p>
              </div>
            </Box>
          )}
        {emailInputErrorType === EMAIL_INPUT_ERROR_TYPE.CONFLICT_ERROR && (
          <Box className="bg-background-1 p-8 m-8 text-base rounded">
            <ul>
              <li>稀に大学が過去のアドレスを再利用している場合があります。</li>
              <li>
                登録に心当たりが無い場合は
                <CommonLink
                  href={INQUIRY}
                  className="hover:underline hover:decoration-primary-2 text-primary-2"
                  children="お問合せ"
                />
                ください。
              </li>
            </ul>
          </Box>
        )}
        <div className="mt-16 flex flex-col justify-center text-base">
          <div className="flex justify-center">
            <CommonLink
              href="/terms"
              className="block text-center text-primary-2 hover:underline hover:decoration-primary-2"
              target="_blank"
            >
              外資就活ドットコム利用規約
            </CommonLink>
            ・
            <CommonLink
              href="/service_privacy_policy"
              className="block text-center hover:underline hover:decoration-primary-2 text-primary-2"
              target="_blank"
            >
              プライバシーポリシー
            </CommonLink>
          </div>
          <div className="flex justify-center text-black-1">
            <CommonLink
              href={`${toLiigaURL}/terms`}
              className="block text-center hover:underline hover:decoration-primary-2 text-primary-2"
              target="_blank"
            >
              外資就活ネクスト利用規約
            </CommonLink>
            ・
            <CommonLink
              href={`${toLiigaURL}/privacy`}
              className="block text-center hover:underline hover:decoration-primary-2 text-primary-2"
              target="_blank"
            >
              個人情報保護方針
            </CommonLink>
          </div>
        </div>
        <div className="text-base text-black-1 mt-8 text-center w-full pc:w-[550px] mx-auto flex flex-col gap-8">
          <p>
            ※
            登録手続きが完了していない場合、手続き完了のためのご案内メールをお送りすることがあります。
          </p>
          {isNewRegisterFlow && (
            <p>
              ※
              本サービスをご利用になるには四年制大学、大学院、高専本科4年次以降、情報系専攻の短期大学・専門学校に在籍している必要があります。
              <br />※
              会員登録後、大学メールアドレスまたは学生証（国内大学に限る）による認証が必要になります。
            </p>
          )}
        </div>

        <div className="mt-16 text-center">
          <BasicButton
            label="同意して登録する"
            className="pc:w-[350px] mx-auto"
            isPrimary={true}
            type="submit"
            onClick={handleRegisterClick}
          />
        </div>
        <div className="text-base text-black-1 text-center mt-16 w-full pc:w-[450px] mx-auto">
          <p>
            ※
            卒業後もキャリア支援サービス外資就活ネクストのアカウントとして利用できます。
          </p>
        </div>
        {!isNewRegisterFlow && (
          <div className="mt-24 p-24 text-center bg-background-1">
            <p className="text-base text-black-1 mb-8">
              大学メールアドレスをお持ちでない方は、学生証をアップロードすることでもご登録いただけます。
            </p>
            <CommonLink
              href={`/student_card${introduceQueryParamUrl}`}
              className="w-full inline-block p-8 bg-white text-primary-1 rounded border border-primary-1 text-center mb-8 hover:bg-background-3 duration-200"
            >
              学生証をアップロード
            </CommonLink>

            <p className="text-sm text-black-1">
              対象は国内大学に在学中の方に限ります。
            </p>
          </div>
        )}
        <div className="mt-24 flex justify-center gap-8 text-base">
          <p className="text-black-1">すでにアカウントをお持ちですか？</p>
          <CommonLink
            href="/login"
            className="block text-primary-2 hover:underline hover:decoration-primary-2"
          >
            ログインする
          </CommonLink>
        </div>
        <div className="mt-36 grid grid-cols-1 pc:grid-cols-3 gap-16 text-center">
          <div>
            <Image
              src={REGISTER_COMPANY_BENEFIT_IMAGE_PATH}
              className="mx-auto mb-16"
              width={120}
              height={120}
              alt={'company-benefit'}
            />
            <p className="text-primary-1 text-lg font-bold">
              志望企業の採用情報を見逃さない
            </p>
            <p className="text-base text-black-2">
              マッキンゼー、ゴールドマン・サックス、P&G、Microsoft、三菱商事、電通、サイバーエージェントなど、厳選企業の募集情報を受け取れます。
            </p>
          </div>
          <div>
            <Image
              src={REGISTER_SCOUT_BENEFIT_IMAGE_PATH}
              className="mx-auto mb-16"
              width={120}
              height={120}
              alt={'scout-benefit'}
            />
            <p className="text-primary-1 text-lg font-bold">
              厳選企業からの限定スカウトが受け取れる
            </p>
            <p className="text-base text-black-2">
              外資就活でしか受け取れない厳選企業からの、選考優遇のあるスカウトが多数送付されています。
            </p>
          </div>
          <div>
            <div>
              <Image
                src={REGISTER_COMMUNITY_BENEFIT_IMAGE_PATH}
                className="mx-auto mb-16"
                width={120}
                height={120}
                alt={'community-benefit'}
              />
            </div>
            <p className="text-primary-1 text-lg font-bold">
              体験記を読んで、難関企業の選考対策を
            </p>
            <p className="text-base text-black-2">
              エントリーシートや、面接で聞かれた質問など5,000本以上掲載。内定した先輩の体験記を読んで、選考突破のコツを学びましょう。
            </p>
          </div>
        </div>
      </Box>
    </Box>
  );
};
